import {
  ISOLATED_SYNTHETIC_EMULATOR_USER,
  LOCAL_EMULATOR_HOST,
  LOCAL_EMULATOR_PORTS,
  LOCAL_EMULATOR_USER,
  resolveAlternateLocalEmulatorPorts,
} from './emulatorConfig';

/**
 * LOCAL DEV MOCK MODE flag — HARD dev-gated.
 *
 * Returns true ONLY when BOTH conditions hold:
 *   1. `import.meta.env.DEV` — Vite sets this to `false` in a production
 *      `vite build`, so every branch guarded by this is statically
 *      dead-code-eliminated from the prod bundle. It can NEVER be true in
 *      production, regardless of env vars.
 *   2. `VITE_LOCAL_MODE === '1'` — an explicit opt-in that is never set in
 *      the prod build pipeline.
 */
export function isLocalMockMode(): boolean {
  try {
    const env = import.meta.env as { DEV?: boolean; VITE_LOCAL_MODE?: string };
    return Boolean(env.DEV) && env.VITE_LOCAL_MODE === '1';
  } catch {
    return false;
  }
}

/**
 * Deterministic browser-test mode for the simulation editor.
 */
export function isSimulationE2eMode(): boolean {
  try {
    const env = import.meta.env as {
      DEV?: boolean;
      VITE_LOCAL_MODE?: string;
      VITE_E2E?: string;
    };
    return Boolean(env.DEV)
      && env.VITE_LOCAL_MODE === '1'
      && env.VITE_E2E === '1';
  } catch {
    return false;
  }
}

/**
 * LOCAL DEV EMULATOR MODE — same hard dev-gate, opt-in via VITE_USE_EMULATORS=1.
 * Fails closed if VITE_ISOLATED_SYNTHETIC_FIXTURE=1 is set without VITE_USE_EMULATORS=1.
 */
export function isEmulatorMode(): boolean {
  let env: {
    DEV?: boolean;
    VITE_USE_EMULATORS?: string;
    VITE_ISOLATED_SYNTHETIC_FIXTURE?: string;
  } | null = null;
  try {
    env = import.meta.env;
  } catch {
    return false;
  }
  if (!env) return false;
  if (env.VITE_ISOLATED_SYNTHETIC_FIXTURE === '1' && (!env.DEV || env.VITE_USE_EMULATORS !== '1')) {
    throw new Error(
      'Isolated synthetic fixture mode requires DEV and VITE_USE_EMULATORS=1; refusing cloud fallback.',
    );
  }
  return Boolean(env.DEV) && env.VITE_USE_EMULATORS === '1';
}

/**
 * Dedicated synthetic-only emulator user/tenant selector for isolated browser
 * walkthroughs (`VITE_ISOLATED_SYNTHETIC_FIXTURE=1`). Ensures the browser
 * auto-signs into `synthetic-audit@datacrate.io` (`uid: synthetic-audit-user`)
 * rather than `rich-test-user`, and fails closed if any browser emulator flag
 * or isolated port is missing.
 */
export function isIsolatedSyntheticFixtureMode(): boolean {
  let env: {
    DEV?: boolean;
    VITE_USE_EMULATORS?: string;
    VITE_ISOLATED_SYNTHETIC_FIXTURE?: string;
    VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS?: string;
    VITE_FIREBASE_AUTH_EMULATOR_PORT?: string;
    VITE_FIRESTORE_EMULATOR_PORT?: string;
    VITE_FIREBASE_STORAGE_EMULATOR_PORT?: string;
  } | null = null;
  try {
    env = import.meta.env;
  } catch {
    return false;
  }
  if (!env || env.VITE_ISOLATED_SYNTHETIC_FIXTURE !== '1') {
    return false;
  }
  if (
    !env.DEV ||
    env.VITE_USE_EMULATORS !== '1' ||
    env.VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS !== '1' ||
    !env.VITE_FIREBASE_AUTH_EMULATOR_PORT ||
    !env.VITE_FIRESTORE_EMULATOR_PORT ||
    !env.VITE_FIREBASE_STORAGE_EMULATOR_PORT
  ) {
    throw new Error(
      'Isolated synthetic fixture mode requires DEV, VITE_USE_EMULATORS=1, VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS=1, and explicit VITE_FIREBASE_AUTH_EMULATOR_PORT, VITE_FIRESTORE_EMULATOR_PORT, and VITE_FIREBASE_STORAGE_EMULATOR_PORT; refusing cloud or default-emulator fallback.',
    );
  }
  return true;
}

export const LOCAL_DEV_EMAIL = isIsolatedSyntheticFixtureMode()
  ? ISOLATED_SYNTHETIC_EMULATOR_USER.email
  : LOCAL_EMULATOR_USER.email;
export const LOCAL_DEV_PASSWORD = isIsolatedSyntheticFixtureMode()
  ? ISOLATED_SYNTHETIC_EMULATOR_USER.password
  : LOCAL_EMULATOR_USER.password;

export const EMULATOR_HOST = LOCAL_EMULATOR_HOST;

function browserEmulatorPorts(): typeof LOCAL_EMULATOR_PORTS | ReturnType<typeof resolveAlternateLocalEmulatorPorts> {
  let env: {
    DEV?: boolean;
    VITE_USE_EMULATORS?: string;
    VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS?: string;
    VITE_ISOLATED_SYNTHETIC_FIXTURE?: string;
    VITE_FIREBASE_AUTH_EMULATOR_PORT?: string;
    VITE_FIRESTORE_EMULATOR_PORT?: string;
    VITE_FIREBASE_STORAGE_EMULATOR_PORT?: string;
  } | null = null;
  try {
    env = import.meta.env;
  } catch {
    return LOCAL_EMULATOR_PORTS;
  }
  if (!env) return LOCAL_EMULATOR_PORTS;
  if (env.VITE_ISOLATED_SYNTHETIC_FIXTURE === '1') {
    if (
      !env.DEV ||
      env.VITE_USE_EMULATORS !== '1' ||
      env.VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS !== '1'
    ) {
      throw new Error(
        'Isolated synthetic fixture mode requires DEV, VITE_USE_EMULATORS=1, and VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS=1; refusing cloud or default-emulator fallback.',
      );
    }
    const ports = resolveAlternateLocalEmulatorPorts({
      auth: env.VITE_FIREBASE_AUTH_EMULATOR_PORT,
      firestore: env.VITE_FIRESTORE_EMULATOR_PORT,
      storage: env.VITE_FIREBASE_STORAGE_EMULATOR_PORT,
    });
    if (
      ports.auth === LOCAL_EMULATOR_PORTS.auth ||
      ports.firestore === LOCAL_EMULATOR_PORTS.firestore ||
      ports.storage === LOCAL_EMULATOR_PORTS.storage
    ) {
      throw new Error(
        'Isolated synthetic fixture mode forbids default LOCAL_EMULATOR_PORTS (9099/8080/9199).',
      );
    }
    return ports;
  }
  if (env.VITE_SIMULATION_AUTH_E2E_EMULATOR_PORTS !== '1') {
    return LOCAL_EMULATOR_PORTS;
  }
  if (!env.DEV || env.VITE_USE_EMULATORS !== '1') {
    throw new Error(
      'Alternate Firebase emulator ports require a Vite development emulator build.',
    );
  }
  return resolveAlternateLocalEmulatorPorts({
    auth: env.VITE_FIREBASE_AUTH_EMULATOR_PORT,
    firestore: env.VITE_FIRESTORE_EMULATOR_PORT,
    storage: env.VITE_FIREBASE_STORAGE_EMULATOR_PORT,
  });
}

export const EMULATOR_PORTS = browserEmulatorPorts();
